import React, { useState, useEffect } from 'react';
import { Users, Home, Calendar, Bell, TrendingUp, Activity, MessageSquare } from 'lucide-react';

import Icon from '../../../components/AppIcon';
import { supabase } from '../../../lib/supabase';
import Skeleton from '../../../components/ui/Skeleton';


const DashboardStats = ({ stats, loading, onRefresh }) => {
  const [requestStats, setRequestStats] = useState({
    totalInquiries: 0,
    newInquiries: 0,
    inProgressInquiries: 0,
    respondedInquiries: 0
  });
  const [requestsLoading, setRequestsLoading] = useState(true);
  const [recentRequests, setRecentRequests] = useState([]);

  // Fetch detailed request statistics
  const fetchRequestStats = async () => {
    setRequestsLoading(true);
    try {
      // Get all property inquiries with detailed information
      const { data: inquiries, error } = await supabase?.from('property_inquiries')
        ?.select(`
          *,
          client:user_profiles!client_id(full_name, email),
          property:properties(title, address)
        `)
        ?.order('created_at', { ascending: false });

      if (error) {
        console.error('Erreur lors du chargement des demandes:', error);
        throw error;
      }

      const allInquiries = inquiries || [];
      
      // Calculate statistics
      const statsData = {
        totalInquiries: allInquiries?.length,
        newInquiries: allInquiries?.filter(i => i?.status === 'new')?.length,
        inProgressInquiries: allInquiries?.filter(i => i?.status === 'in_progress')?.length,
        respondedInquiries: allInquiries?.filter(i => i?.status === 'responded')?.length
      };

      setRequestStats(statsData);
      setRecentRequests(allInquiries?.slice(0, 5)); // Get 5 most recent requests
      
      console.log('📊 Request statistics loaded:', statsData);
    } catch (error) {
      console.error('Error fetching request stats:', error);
    } finally {
      setRequestsLoading(false);
    }
  };

  useEffect(() => {
    fetchRequestStats();
  }, []);

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'XOF',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    })?.format(amount || 0);
  };

  const statCards = [
    {
      title: 'Utilisateurs Total',
      value: stats?.totalUsers || 0,
      icon: Users,
      color: 'bg-blue-500',
      change: '+12%',
      changeType: 'positive'
    },
    {
      title: 'Propriétés Actives',
      value: stats?.totalProperties || 0,
      icon: Home,
      color: 'bg-green-500',
      change: '+8%',
      changeType: 'positive'
    },
    {
      title: 'Rendez-vous',
      value: stats?.totalAppointments || 0,
      icon: Calendar,
      color: 'bg-purple-500',
      change: '+15%',
      changeType: 'positive'
    },
    {
      title: 'Demandes Clients',
      value: requestStats?.totalInquiries || 0,
      icon: MessageSquare,
      color: 'bg-orange-500',
      change: `${requestStats?.newInquiries} nouvelles`,
      changeType: requestStats?.newInquiries > 0 ? 'positive' : 'neutral'
    },
    {
      title: 'Notifications',
      value: stats?.totalNotifications || 0,
      icon: Bell,
      color: 'bg-red-500',
      change: 'Temps réel',
      changeType: 'neutral'
    },
    {
      title: 'Revenus Total',
      value: formatCurrency(stats?.totalRevenue),
      icon: TrendingUp,
      color: 'bg-indigo-500',
      change: '+5%',
      changeType: 'positive'
    }
  ];

  if (loading || requestsLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
        {[...Array(6)]?.map((_, index) => (
          <div key={index} className="bg-card border border-border rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <Skeleton className="h-4 w-1/2" />
              <Skeleton className="h-8 w-8 rounded-lg" />
            </div>
            <Skeleton className="h-8 w-1/3 mb-2" />
            <Skeleton className="h-4 w-1/4" />
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Main Statistics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {statCards?.map((card, index) => {
          const Icon = card?.icon;
          return (
            <div key={index} className="bg-white rounded-lg shadow-sm p-6 hover:shadow-md transition-shadow">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <p className="text-sm font-medium text-gray-600">{card?.title}</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {typeof card?.value === 'string' ? card?.value : card?.value?.toLocaleString()}
                  </p>
                </div>
                <div className={`${card?.color} p-3 rounded-lg`}>
                  <Icon className="h-6 w-6 text-white" />
                </div>
              </div>
              <div className="flex items-center">
                <span className={`text-sm font-medium ${
                  card?.changeType === 'positive' ? 'text-green-600' : 
                  card?.changeType === 'negative' ? 'text-red-600' : 'text-gray-600'
                }`}>
                  {card?.change}
                </span>
              </div>
            </div>
          );
        })}
      </div>

      {/* Recent Requests Section */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">
              Demandes Récentes des Clients
            </h3>
            <p className="text-sm text-gray-600">
              Les dernières demandes de renseignements reçues
            </p>
          </div>
          <button
            onClick={fetchRequestStats}
            className="text-blue-600 hover:text-blue-800 text-sm font-medium"
          >
            Actualiser
          </button>
        </div>

        {/* Request Statistics Summary */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6 p-4 bg-gray-50 rounded-lg">
          <div className="text-center">
            <p className="text-2xl font-bold text-blue-600">{requestStats?.totalInquiries}</p>
            <p className="text-sm text-gray-600">Total</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-orange-600">{requestStats?.newInquiries}</p>
            <p className="text-sm text-gray-600">Nouvelles</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-yellow-600">{requestStats?.inProgressInquiries}</p>
            <p className="text-sm text-gray-600">En cours</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-green-600">{requestStats?.respondedInquiries}</p>
            <p className="text-sm text-gray-600">Traitées</p>
          </div>
        </div>

        {/* Recent Requests List */}
        {recentRequests?.length === 0 ? (
          <div className="text-center py-8">
            <MessageSquare className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h4 className="text-lg font-medium text-gray-900 mb-2">
              Aucune demande trouvée
            </h4>
            <p className="text-gray-600">
              Les nouvelles demandes de renseignements apparaîtront ici
            </p>
            <div className="mt-4 p-3 bg-blue-50 rounded-lg">
              <p className="text-sm text-blue-700">
                💡 <strong>Info:</strong> Les demandes sont créées lorsque les clients utilisent 
                les formulaires de contact sur les pages de propriétés individuelles.
              </p>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            {recentRequests?.map((request) => (
              <div 
                key={request?.id}
                className="flex items-start justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <span className="font-medium text-gray-900">
                      {request?.client?.full_name}
                    </span>
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                      request?.status === 'new' ?'bg-orange-100 text-orange-800' 
                        : request?.status === 'in_progress' ?'bg-yellow-100 text-yellow-800'
                        : request?.status === 'responded' ?'bg-green-100 text-green-800' :'bg-gray-100 text-gray-800'
                    }`}>
                      {request?.status === 'new' ? 'Nouvelle' : 
                       request?.status === 'in_progress' ? 'En cours' :
                       request?.status === 'responded' ? 'Traitée' : request?.status}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 mb-1">
                    <strong>Propriété:</strong> {request?.property?.title}
                  </p>
                  <p className="text-xs text-gray-500">
                    {request?.message?.substring(0, 100)}{request?.message?.length > 100 ? '...' : ''}
                  </p>
                </div>
                <div className="text-right text-sm text-gray-500">
                  {new Date(request?.created_at)?.toLocaleDateString('fr-FR')}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* System Activity Overview */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">
            Activité Système Récente
          </h3>
          <button
            onClick={onRefresh}
            className="text-blue-600 hover:text-blue-800 text-sm font-medium"
          >
            Actualiser
          </button>
        </div>
        
        {stats?.recentActivity?.length === 0 ? (
          <div className="text-center py-8">
            <Activity className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">Aucune activité récente</p>
          </div>
        ) : (
          <div className="space-y-3">
            {stats?.recentActivity?.slice(0, 5)?.map((activity) => (
              <div key={activity?.id} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                <Activity className="h-4 w-4 text-gray-400" />
                <div className="flex-1">
                  <p className="text-sm text-gray-900">
                    <strong>{activity?.action}</strong> sur {activity?.entity_type}
                  </p>
                  <p className="text-xs text-gray-500">
                    {new Date(activity?.created_at)?.toLocaleString('fr-FR')}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default DashboardStats;